import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import config from "../../config";
import Labels from "../../services/apiLabels";
import { UPDATE_AUTH } from "../../constant";
import AccountShell from "./AccountShell";
import CountrySelect from "../../components/CountrySelect";
import PhoneWithCountryCode from "../../components/PhoneWithCountryCode";
import {
  isCountryEnabled,
  resolveDialForCountryName,
} from "../../utils/countries";
import {
  buildAuthProfilePatch,
  getAuthAvatarUrl,
  getAuthDisplayName,
  getAuthInitials,
  getAuthToken,
  getAuthUserId,
  pickUserImage,
  resolveMediaUrl,
  withCacheBust,
} from "../../utils/userIdentity";
import { isDealerUser, pickUserType } from "../../utils/userTypes";
import {
  BUSINESS_TYPE_OPTIONS,
  NEXT_GEAR_OPTIONS,
  EMPTY_CLIENT_PROFILE_FORM,
  EMPTY_DEALER_PROFILE_FORM,
  buildClientProfileFields,
  buildDealerProfileFields,
  buildProfileBody,
  mapUserToClientProfileForm,
  mapUserToDealerProfileForm,
  pickProfileLocationLists,
} from "../../utils/profileForms";
import { scrollTop } from "../../utils/window";

function pickImageFromResponse(data) {
  const candidates = [
    data?.data?.user?.image,
    data?.data?.user?.profile_image,
    data?.user?.image,
    data?.user?.profile_image,
    data?.data?.image,
    data?.data?.profile_image,
    data?.image,
    data?.profile_image,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (typeof value === "string" && value && !Array.isArray(value)) {
      return value;
    }
  }
  return "";
}

function isProfileUpdateError(data) {
  if (data?.message?.type === "error") return true;
  const imageErr = data?.data?.image;
  return Array.isArray(imageErr) && imageErr.length > 0;
}

function profileUpdateErrorMessage(data, fallback) {
  const imageErr = data?.data?.image;
  if (Array.isArray(imageErr) && imageErr[0]) return String(imageErr[0]);
  if (typeof imageErr === "string" && imageErr) return imageErr;
  return (
    data?.message?.description ||
    data?.message?.heading ||
    fallback
  );
}

function emptyForm(isDealer) {
  return isDealer
    ? { ...EMPTY_DEALER_PROFILE_FORM }
    : { ...EMPTY_CLIENT_PROFILE_FORM };
}

function ProfilePage({ auth, dispatch }) {
  const isDealer = isDealerUser(auth);
  const userId = getAuthUserId(auth);
  const token = getAuthToken(auth);
  const [form, setForm] = useState(() => emptyForm(isDealerUser(auth)));
  const [locationLists, setLocationLists] = useState({
    county: [],
    country: [],
  });
  const [identity, setIdentity] = useState({ username: "", email: "" });
  const [imagePreview, setImagePreview] = useState(() => {
    const fromAuth = getAuthAvatarUrl(auth);
    return fromAuth ? withCacheBust(fromAuth) : "";
  });
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [message, setMessage] = useState(null);
  const formRef = useRef(emptyForm(isDealerUser(auth)));
  const fileInputRef = useRef(null);

  useEffect(() => {
    scrollTop();
  }, []);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [imagePreview]);

  useEffect(() => {
    if (!userId || !token) {
      setLoading(false);
      const fromAuth = getAuthAvatarUrl(auth);
      if (fromAuth) {
        setImagePreview(withCacheBust(fromAuth));
      }
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${config.baseUrl}${Labels.getupdateprofile}${userId}`, {
        headers: { Authorization: `Token ${token}` },
      })
      .then((response) => {
        if (cancelled) return;
        const payload = response?.data || {};
        const user =
          payload?.data?.user ||
          payload?.user ||
          payload?.data ||
          payload ||
          {};
        const dealer = isDealerUser(auth) || isDealerUser(user);
        const nextForm = dealer
          ? mapUserToDealerProfileForm(user)
          : mapUserToClientProfileForm(user);
        setForm(nextForm);
        formRef.current = nextForm;
        setLocationLists(pickProfileLocationLists(payload));
        setIdentity({
          username: user.username || auth.username || "",
          email: user.email || auth.email || "",
        });
        const img =
          pickUserImage(user, payload?.data, payload) ||
          getAuthAvatarUrl(auth);
        const raw =
          user.image ||
          user.image_url ||
          user.profile_image ||
          user.profile_image_url ||
          user.avatar ||
          "";
        const resolvedType = pickUserType(user, payload);
        dispatch({
          type: UPDATE_AUTH,
          payload: {
            ...buildAuthProfilePatch(nextForm, raw || undefined),
            ...(resolvedType != null ? { type: resolvedType } : {}),
          },
        });
        if (img) {
          setImagePreview(withCacheBust(resolveMediaUrl(img) || img));
          setAvatarFailed(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        const dealer = isDealerUser(auth);
        const nextForm = dealer
          ? mapUserToDealerProfileForm(auth)
          : mapUserToClientProfileForm(auth);
        setForm(nextForm);
        formRef.current = nextForm;
        setIdentity({
          username: auth.username || "",
          email: auth.email || "",
        });
        const fromAuth = getAuthAvatarUrl(auth);
        if (fromAuth) {
          setImagePreview(withCacheBust(fromAuth));
        }
        setMessage({
          type: "err",
          text: "Could not load full profile. Showing signed-in details.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, token, dispatch]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onCountryChange = (countryName) => {
    setForm((prev) => ({
      ...prev,
      country: countryName,
      phone_dial: resolveDialForCountryName(countryName, prev.phone_dial),
    }));
  };

  const onPhoneDialChange = (dial, dialCountry) => {
    setForm((prev) => ({
      ...prev,
      phone_dial: dial,
      ...(dialCountry?.name && isCountryEnabled(dialCountry.code)
        ? { country: dialCountry.name }
        : {}),
    }));
  };

  const onPhoneNationalChange = (national) => {
    setForm((prev) => ({ ...prev, phone_national: national }));
  };

  const syncAvatarToAuth = (imagePath) => {
    if (!imagePath) return "";
    dispatch({
      type: UPDATE_AUTH,
      payload: buildAuthProfilePatch(formRef.current, imagePath),
    });
    const preview = withCacheBust(resolveMediaUrl(imagePath));
    setImagePreview(preview);
    setAvatarFailed(false);
    return preview;
  };

  const syncProfileFieldsToAuth = (imagePath) => {
    const current = formRef.current || {};
    const typeFromForm = isDealer
      ? Number(current.business_type) || pickUserType(auth)
      : pickUserType(auth) ?? 6;
    dispatch({
      type: UPDATE_AUTH,
      payload: {
        ...buildAuthProfilePatch(current, imagePath || undefined),
        ...(typeFromForm != null ? { type: typeFromForm } : {}),
      },
    });
  };

  const fetchSavedAvatar = async () => {
    const response = await axios.get(
      `${config.baseUrl}${Labels.getupdateprofile}${userId}`,
      { headers: { Authorization: `Token ${token}` } }
    );
    const user =
      response?.data?.data?.user ||
      response?.data?.user ||
      response?.data?.data ||
      response?.data ||
      {};
    return user.image || user.profile_image || "";
  };

  const putProfile = async (imageValue, successText) => {
    const url = `${config.baseUrl}${Labels.getupdateprofile}${userId}/`;
    const fields = isDealer
      ? buildDealerProfileFields(formRef.current)
      : buildClientProfileFields(formRef.current);
    const payload = buildProfileBody(fields, imageValue);

    let response;
    let data;
    if (payload instanceof FormData) {
      response = await fetch(url, {
        method: "PUT",
        headers: { Authorization: `Token ${token}` },
        body: payload,
      });
      data = await response.json().catch(() => ({}));
    } else {
      response = await axios.put(url, payload, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      data = response?.data;
    }

    if (
      (payload instanceof FormData && !response.ok) ||
      isProfileUpdateError(data)
    ) {
      throw new Error(
        profileUpdateErrorMessage(data, "Profile update failed.")
      );
    }

    if (successText) {
      setMessage({ type: "ok", text: successText });
    }
    return data;
  };

  const onPickImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userId || !token) return;
    const previousPreview = imagePreview;
    setImagePreview(URL.createObjectURL(file));
    setImageUploading(true);
    setMessage(null);
    try {
      const data = await putProfile(file, "Profile photo updated.");
      let saved = pickImageFromResponse(data);
      if (!saved) {
        saved = await fetchSavedAvatar();
      }
      if (saved) {
        syncAvatarToAuth(saved);
      } else {
        setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      }
    } catch (e) {
      setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      setMessage({
        type: "err",
        text:
          e?.message ||
          "Could not update profile photo. Please try again.",
      });
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!userId || !token) return;
    const previousPreview = imagePreview;
    setSaving(true);
    setMessage(null);
    try {
      const data = await putProfile(undefined, "Profile updated successfully.");
      const saved = pickImageFromResponse(data);
      if (saved) {
        syncAvatarToAuth(saved);
      } else {
        const fromAuth = getAuthAvatarUrl(auth);
        setImagePreview(
          previousPreview || (fromAuth ? withCacheBust(fromAuth) : "")
        );
        setAvatarFailed(false);
        syncProfileFieldsToAuth();
      }
    } catch (e) {
      setImagePreview(previousPreview || getAuthAvatarUrl(auth) || "");
      setMessage({
        type: "err",
        text: "Could not update profile. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccountShell
      title="Edit profile"
      lead={
        isDealer
          ? "Update your dealership and trade-floor contact details."
          : "Keep your contact details up to date for valuations and appointments."
      }
    >
      {message ? (
        <p
          className={`pi-account-alert pi-account-alert--${
            message.type === "ok" ? "ok" : "err"
          }`}
        >
          {message.text}
        </p>
      ) : null}

      {loading ? (
        <p className="pi-account-note">Loading profile…</p>
      ) : (
        <form className="pi-account-form" onSubmit={onSubmit}>
          <div className="pi-account-avatar-edit">
            <span className="pi-account-avatar-edit__img">
              {imagePreview && !avatarFailed ? (
                <img
                  src={imagePreview}
                  alt=""
                  onError={() => setAvatarFailed(true)}
                />
              ) : (
                getAuthInitials(auth)
              )}
            </span>
            <div>
              <p className="pi-account-note" style={{ marginBottom: "0.5rem" }}>
                {getAuthDisplayName(auth)}
              </p>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <label className="pi-account-btn pi-account-btn--ghost">
                  {imageUploading ? "Updating…" : "Change photo"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={imageUploading || saving}
                    onChange={onPickImage}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="pi-account-form__row">
            <div className="pi-account-field">
              <label htmlFor="profile-first">First name</label>
              <input
                id="profile-first"
                name="first_name"
                value={form.first_name}
                onChange={onChange}
              />
            </div>
            <div className="pi-account-field">
              <label htmlFor="profile-last">Last name</label>
              <input
                id="profile-last"
                name="last_name"
                value={form.last_name}
                onChange={onChange}
              />
            </div>
          </div>

          <div className="pi-account-field">
            <label htmlFor="profile-username">Username (can&apos;t be changed)</label>
            <input
              id="profile-username"
              value={identity.username}
              readOnly
              disabled
            />
          </div>
          <div className="pi-account-field">
            <label htmlFor="profile-email">Email (can&apos;t be changed)</label>
            <input
              id="profile-email"
              type="email"
              value={identity.email}
              readOnly
              disabled
            />
          </div>

          {isDealer ? (
            <>
              <div className="pi-account-field">
                <label htmlFor="profile-company">Company name</label>
                <input
                  id="profile-company"
                  name="company_name"
                  value={form.company_name || ""}
                  onChange={onChange}
                />
              </div>
              <div className="pi-account-field">
                <label htmlFor="profile-mobile">Mobile number</label>
                <PhoneWithCountryCode
                  id="profile-mobile-dealer"
                  dial={form.phone_dial}
                  national={form.phone_national}
                  countryName={form.country}
                  onDialChange={onPhoneDialChange}
                  onNationalChange={onPhoneNationalChange}
                  disabled={saving || imageUploading}
                />
              </div>
              <div className="pi-account-field">
                <label htmlFor="profile-address">Address</label>
                <input
                  id="profile-address"
                  name="address"
                  value={form.address || ""}
                  onChange={onChange}
                />
              </div>
              <div className="pi-account-form__row">
                <div className="pi-account-field">
                  <label htmlFor="profile-city">City</label>
                  <input
                    id="profile-city"
                    name="city"
                    value={form.city || ""}
                    onChange={onChange}
                  />
                </div>
                <div className="pi-account-field">
                  <label htmlFor="profile-county">County</label>
                  <select
                    id="profile-county"
                    name="county"
                    value={form.county || ""}
                    onChange={onChange}
                  >
                    <option value="">Select county</option>
                    {locationLists.county.map((item) => (
                      <option key={item.id} value={String(item.id)}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="pi-account-form__row">
                <div className="pi-account-field">
                  <label htmlFor="profile-country">Country</label>
                  <CountrySelect
                    id="profile-country-dealer"
                    name="country"
                    value={form.country}
                    onChange={onCountryChange}
                    disabled={saving || imageUploading}
                  />
                </div>
                <div className="pi-account-field">
                  <label htmlFor="profile-postcode">Postcode</label>
                  <input
                    id="profile-postcode"
                    name="postcode"
                    value={form.postcode || ""}
                    onChange={onChange}
                  />
                </div>
              </div>
              <div className="pi-account-field">
                <label htmlFor="profile-garage">Garage code</label>
                <input
                  id="profile-garage"
                  name="garagecode"
                  value={form.garagecode || ""}
                  onChange={onChange}
                />
              </div>
              <div className="pi-account-form__row">
                <div className="pi-account-field">
                  <label htmlFor="profile-business">Business type</label>
                  <select
                    id="profile-business"
                    name="business_type"
                    value={form.business_type || "1"}
                    onChange={onChange}
                  >
                    {BUSINESS_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="pi-account-field">
                  <label htmlFor="profile-nextgear">
                    Next Gear Capital client
                  </label>
                  <select
                    id="profile-nextgear"
                    name="client_type"
                    value={form.client_type || "2"}
                    onChange={onChange}
                  >
                    {NEXT_GEAR_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="pi-account-field">
                <label htmlFor="profile-mobile">Mobile number</label>
                <PhoneWithCountryCode
                  id="profile-mobile"
                  dial={form.phone_dial}
                  national={form.phone_national}
                  countryName={form.country}
                  onDialChange={onPhoneDialChange}
                  onNationalChange={onPhoneNationalChange}
                  disabled={saving || imageUploading}
                />
              </div>
              <div className="pi-account-field">
                <label htmlFor="profile-address">Address</label>
                <input
                  id="profile-address"
                  name="address"
                  value={form.address}
                  onChange={onChange}
                />
              </div>
              <div className="pi-account-form__row">
                <div className="pi-account-field">
                  <label htmlFor="profile-country">Country</label>
                  <CountrySelect
                    id="profile-country"
                    name="country"
                    value={form.country}
                    onChange={onCountryChange}
                    disabled={saving || imageUploading}
                  />
                </div>
                <div className="pi-account-field">
                  <label htmlFor="profile-postcode">Postcode</label>
                  <input
                    id="profile-postcode"
                    name="postcode"
                    value={form.postcode}
                    onChange={onChange}
                  />
                </div>
              </div>
            </>
          )}

          <button
            type="submit"
            className="pi-account-btn"
            disabled={saving || imageUploading}
          >
            {saving ? "Saving…" : "Save profile"}
          </button>
        </form>
      )}
    </AccountShell>
  );
}

const mapStateToProps = (state) => ({ auth: state.auth });
export default connect(mapStateToProps)(ProfilePage);
